import { useMemo } from 'react';
import { DonutChartCard, NeighborhoodUnitsChart, PipelineStageChartCard, VerticalBarChartCard } from './components/DashboardCharts';
import { KpiCard } from './components/KpiCard';
import { MapWidget } from './components/MapWidget';
import { dashboardTooltips } from './gmfDeveloperPipeline.constants';
import type { GmfApplicationSummary } from './types/gmfDeveloperPipeline.types';
import { buildGmfDashboardMetrics } from './utils/dashboardMetrics';
import { formatAverageUnits, formatCurrency, formatNumber } from './utils/formatters';

interface GMFDeveloperPipelineDashboardProps {
  data: {
    data?: GmfApplicationSummary[];
  };
}

export const GMFDeveloperPipelineDashboard = ({ data }: GMFDeveloperPipelineDashboardProps) => {
  const applications = data?.data ?? [];
  const metrics = useMemo(() => buildGmfDashboardMetrics(applications), [applications]);

  return (
    <div className='mx-auto max-w-screen-2xl space-y-8 px-4 py-6 sm:px-6 lg:px-8'>
      <div className='border-b border-border pb-5'>
        <p className='text-sm text-muted-foreground'>Greater Milwaukee Foundation</p>
        <h1 className='text-2xl font-semibold tracking-tight text-foreground'>Developer Pipeline Dashboard</h1>
      </div>

      <section className='grid gap-4 md:grid-cols-2 xl:grid-cols-5'>
        <KpiCard
          label='Total Developers in Pipeline'
          value={formatNumber(metrics.totalDevelopers)}
          tooltip={dashboardTooltips.totalDevelopers}
        />
        <KpiCard label='Total Projects Submitted' value={formatNumber(metrics.totalProjects)} tooltip={dashboardTooltips.totalProjects} />
        <KpiCard label='Total Proposed Units' value={formatNumber(metrics.totalProposedUnits)} tooltip={dashboardTooltips.totalUnits} />
        <KpiCard
          label='Total Development Cost'
          value={formatCurrency(metrics.totalDevelopmentCost)}
          tooltip={dashboardTooltips.developmentCost}
        />
        <KpiCard
          label='Predevelopment Need'
          value={formatCurrency(metrics.totalPreDevelopmentNeed)}
          tooltip={dashboardTooltips.predevelopmentNeed}
        />
      </section>

      <section className='grid gap-4 md:grid-cols-3'>
        <KpiCard
          label='Average Project Size'
          value={`${formatAverageUnits(metrics.averageUnits)} Units / ${formatCurrency(metrics.averageDevelopmentCost)}`}
          tooltip={dashboardTooltips.averageProjectSize}
        />
        <KpiCard
          label='Estimated Cost Per Unit'
          value={formatCurrency(metrics.estimatedCostPerUnit)}
          tooltip={dashboardTooltips.costPerUnit}
        />
        <KpiCard
          label='Total Acquisition Cost'
          value={formatCurrency(metrics.totalAcquisitionCost)}
          tooltip={dashboardTooltips.acquisitionCost}
        />
      </section>

      <section className='grid gap-6 lg:grid-cols-3'>
        <div className='lg:col-span-2'>
          <MapWidget projects={metrics.mapProjects} />
        </div>
        <div className='space-y-6'>
          <VerticalBarChartCard title='Projects by ZIP Code' data={metrics.zipCodeProjects} tooltip={dashboardTooltips.zipCode} />
          <NeighborhoodUnitsChart
            title='Proposed Units by Neighborhood'
            data={metrics.neighborhoodUnits}
            tooltip={dashboardTooltips.neighborhood}
          />
        </div>
      </section>

      <section className='grid gap-6 md:grid-cols-3'>
        <DonutChartCard title='Site Control Status' data={metrics.siteControlStatus} tooltip={dashboardTooltips.siteControl} />
        <DonutChartCard title='Construction Type' data={metrics.constructionTypes} tooltip={dashboardTooltips.constructionType} />
        <PipelineStageChartCard
          title='Pipeline Stage Distribution'
          data={metrics.pipelineStages}
          tooltip={dashboardTooltips.pipelineStage}
        />
      </section>

      <section className='grid gap-6 md:grid-cols-2 xl:grid-cols-4'>
        <DonutChartCard title='ACRE Graduate Breakdown' data={metrics.acreGraduate} tooltip={dashboardTooltips.acreGraduate} />
        <DonutChartCard title='Minority-Owned Developers' data={metrics.minorityOwned} tooltip={dashboardTooltips.minorityOwned} />
        <DonutChartCard title='Neighborhood-Based Orgs' data={metrics.neighborhoodBased} tooltip={dashboardTooltips.neighborhoodBased} />
        <DonutChartCard title='Organization Type' data={metrics.organizationTypes} tooltip={dashboardTooltips.organizationType} />
      </section>
    </div>
  );
};
